import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, matchPath } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Modal } from '../components/Modal';
import Mixpanel from '../lib/mixpanel';

const HOLIDAY_HOURS_PATH = '/profile/holiday-hours';

/** Ignore no-op transitions (e.g. refresh / router re-sync) that don't actually change URL. */
const locationUrlsEquivalent = (a, b) => {
  if (!a || !b) {
    return false;
  }
  return a.pathname === b.pathname && a.search === b.search && a.hash === b.hash;
};

const debug = (...args) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[HolidayHoursNav]', ...args);
  }
};

const HolidayHoursNavigationGuardContext = createContext(null);

export const useHolidayHoursNavigationGuard = () => useContext(HolidayHoursNavigationGuardContext);

export const HolidayHoursNavigationGuardProvider = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const holidayScheduleStatus = useSelector((state) => state.holidayScheduleStatus);

  const [holidayHoursDirty, setHolidayHoursDirty] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveModalReason, setLeaveModalReason] = useState(null);

  const dirtyRef = useRef(false);
  dirtyRef.current = holidayHoursDirty;

  const pendingNavigationRef = useRef(null);
  const unblockRef = useRef(() => {});

  const onHolidayHoursPage = Boolean(
    matchPath(location.pathname, { path: HOLIDAY_HOURS_PATH, exact: true }),
  );

  /** True once we have a successful schedule fetch and the venue has not saved holiday hours yet. */
  const scheduleKnownIncomplete =
    !holidayScheduleStatus.fetching &&
    holidayScheduleStatus.success &&
    !holidayScheduleStatus.error &&
    holidayScheduleStatus.data?.holidaysScheduled !== true;

  const scheduleIncompleteRef = useRef(false);
  scheduleIncompleteRef.current = scheduleKnownIncomplete;

  const leaveFrictionActive = holidayHoursDirty || scheduleKnownIncomplete;
  const shouldBlock = onHolidayHoursPage && leaveFrictionActive;

  useLayoutEffect(() => {
    debug('history.block registration', {
      shouldBlock,
      onHolidayHoursPage,
      holidayHoursDirty,
      scheduleKnownIncomplete,
      holidaysScheduled: holidayScheduleStatus.data?.holidaysScheduled,
      pathname: location.pathname,
    });
    if (!shouldBlock) {
      unblockRef.current = () => {};
      return undefined;
    }
    const unblock = history.block((nextLocation, action) => {
      const current = history.location;
      if (locationUrlsEquivalent(nextLocation, current)) {
        debug('history.block: same URL as current — allow (no modal)', {
          action,
          nextLocation,
          current,
        });
        return undefined;
      }
      debug('history.block fired (navigation intercepted)', {
        action,
        nextLocation,
        pathname: location.pathname,
      });
      pendingNavigationRef.current = { location: nextLocation };
      const reason = dirtyRef.current ? 'dirty' : 'incomplete';
      const intendedDestination = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
      Mixpanel.track('HDS leave modal shown', {
        reason,
        intended_destination: intendedDestination,
        source: 'history',
        history_action: action,
        origin: window.location.origin,
      });
      setLeaveModalReason(reason);
      setLeaveModalOpen(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- blocker deps are shouldBlock-only; log reads pathname
  }, [history, shouldBlock, onHolidayHoursPage, holidayHoursDirty, scheduleKnownIncomplete]);

  useEffect(() => {
    if (!onHolidayHoursPage) {
      debug('left holiday hours route — clearing guard dirty + pending', {
        pathname: location.pathname,
      });
      setHolidayHoursDirty(false);
      setLeaveModalOpen(false);
      setLeaveModalReason(null);
      pendingNavigationRef.current = null;
    }
  }, [onHolidayHoursPage, location.pathname]);

  const confirmLeave = useCallback(() => {
    const pending = pendingNavigationRef.current;
    const destination = pending?.location
      ? `${pending.location.pathname || ''}${pending.location.search || ''}${pending.location.hash || ''}`
      : undefined;
    Mixpanel.track('HDS leave modal leave', {
      reason: leaveModalReason,
      intended_destination: destination,
      origin: window.location.origin,
    });
    pendingNavigationRef.current = null;
    setLeaveModalOpen(false);
    setLeaveModalReason(null);
    unblockRef.current();
    if (pending?.location) {
      history.push(pending.location);
    }
  }, [history, leaveModalReason]);

  const cancelLeave = useCallback(() => {
    Mixpanel.track('HDS leave modal stay', {
      reason: leaveModalReason,
      origin: window.location.origin,
    });
    pendingNavigationRef.current = null;
    setLeaveModalOpen(false);
    setLeaveModalReason(null);
  }, [leaveModalReason]);

  /**
   * Call from NavLink onClick before Mixpanel / hideDrawer. Returns true if navigation was blocked.
   */
  const tryNavigateAway = useCallback(
    (event, to) => {
      const toPath = typeof to === 'string' ? to : to?.pathname;
      const baseInfo = {
        to,
        toPath,
        dirty: dirtyRef.current,
        scheduleKnownIncomplete: scheduleIncompleteRef.current,
        onHolidayHoursPage,
        pathname: location.pathname,
        fullLocation: `${location.pathname}${location.search}${location.hash}`,
      };

      if (!onHolidayHoursPage) {
        debug('tryNavigateAway: no block (not on holiday hours page)', baseInfo);
        return false;
      }
      if (!dirtyRef.current && !scheduleIncompleteRef.current) {
        debug(
          'tryNavigateAway: no block (not dirty and holiday hours already saved or schedule still loading)',
          baseInfo,
        );
        return false;
      }
      if (!event || event.defaultPrevented) {
        debug('tryNavigateAway: no block (missing event or defaultPrevented)', baseInfo);
        return false;
      }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        debug('tryNavigateAway: no block (modified click / not primary button)', {
          ...baseInfo,
          button: event.button,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        });
        return false;
      }

      if (!toPath || toPath.startsWith('#')) {
        debug('tryNavigateAway: no block (invalid to path)', baseInfo);
        return false;
      }

      let pathname;
      let search = '';
      let hash = '';
      try {
        const url = new URL(toPath, window.location.origin);
        pathname = url.pathname;
        search = url.search;
        hash = url.hash;
      } catch (err) {
        debug('tryNavigateAway: no block (URL parse failed)', { ...baseInfo, err: String(err) });
        return false;
      }

      const next = `${pathname}${search}${hash}`;
      const current = `${location.pathname}${location.search}${location.hash}`;
      if (next === current) {
        debug('tryNavigateAway: no block (already on target URL)', { ...baseInfo, next, current });
        return false;
      }

      event.preventDefault();
      event.stopPropagation();

      pendingNavigationRef.current = {
        location: { pathname, search, hash },
      };
      const leaveReason = dirtyRef.current ? 'dirty' : 'incomplete';
      Mixpanel.track('HDS leave modal shown', {
        reason: leaveReason,
        intended_destination: next,
        source: 'nav_click',
        origin: window.location.origin,
      });
      setLeaveModalReason(leaveReason);
      setLeaveModalOpen(true);
      debug('tryNavigateAway: BLOCKED — showing leave modal', { ...baseInfo, next, current });
      return true;
    },
    [onHolidayHoursPage, location.pathname, location.search, location.hash],
  );

  const leaveModalHeading =
    leaveModalReason === 'incomplete' ? 'Finish holiday hours first?' : 'Leave without saving?';

  const leaveModalBody =
    leaveModalReason === 'incomplete'
      ? 'You have not saved your holiday trading hours yet. Please confirm them on this page before continuing elsewhere in the portal.'
      : 'You have unsaved holiday hours changes. If you leave now, those changes will be lost.';

  const setHolidayHoursDirtyLogged = useCallback((next) => {
    debug('setHolidayHoursDirty', {
      next,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    });
    setHolidayHoursDirty(next);
  }, []);

  const value = useMemo(
    () => ({
      setHolidayHoursDirty: setHolidayHoursDirtyLogged,
      tryNavigateAway,
    }),
    [tryNavigateAway, setHolidayHoursDirtyLogged],
  );

  return (
    <HolidayHoursNavigationGuardContext.Provider value={value}>
      {children}
      <Modal
        isOpen={leaveModalOpen}
        onClose={cancelLeave}
        heading={leaveModalHeading}
        confirmLabel='Leave'
        cancelLabel='Stay on this page'
        onConfirm={confirmLeave}
        disabled={false}
        inputStyle={{
          button: { backgroundColor: '#FAD14C', color: theme.colors.charcoalTransparent },
        }}
      >
        {leaveModalBody}
      </Modal>
    </HolidayHoursNavigationGuardContext.Provider>
  );
};

HolidayHoursNavigationGuardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
